/*
 * DecisionHistory.tsx — S57: Decision History Panel
 * API: GET /api/v1/decisions/stats/summary
 */
import { useState, useEffect } from 'react';
import { API_V1, safeFetch, titleCase } from '../constants';

interface DecisionStat {
  exception_type: string;
  framework: string;
  total_decisions: number;
  approval_rate: number;
  avg_ai_confidence: number | null;
  mode?: string;
  last_decision_at?: string | null;
}

export default function DecisionHistory() {
  const [stats, setStats] = useState<DecisionStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = () => {
    setLoading(true);
    setError('');
    safeFetch(`${API_V1}/decisions/stats/summary`, { stats: [] })
      .then(d => { setStats(Array.isArray(d) ? d : d?.stats || []); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="main" style={{ padding: 32 }}><p style={{ color: 'var(--text-secondary)' }}>Loading decision history...</p></div>;
  if (error) return (
    <div className="main" style={{ padding: 32 }}>
      <p style={{ color: 'var(--red)' }}>Error: {error}</p>
      <button onClick={load} style={{ marginTop: 8 }}>Retry</button>
    </div>
  );

  const totalDecisions = stats.reduce((s, d) => s + d.total_decisions, 0);
  const avgApproval = stats.length > 0
    ? stats.reduce((s, d) => s + d.approval_rate * d.total_decisions, 0) / Math.max(totalDecisions, 1)
    : 0;

  const icon = (rate: number) => rate >= 0.8 ? '\u2713' : rate >= 0.5 ? '\u2191' : '\u2717';
  const iconColor = (rate: number) => rate >= 0.8 ? 'var(--green)' : rate >= 0.5 ? 'var(--amber)' : 'var(--red)';

  return (
    <div className="main" style={{ padding: 32, maxWidth: 900 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24 }}>
        <div style={{ padding: '16px 20px', borderRadius: 8, border: '1px solid var(--border-subtle)', background: 'var(--bg-card)' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>Total Decisions</div>
          <div style={{ fontSize: 22, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{totalDecisions}</div>
        </div>
        <div style={{ padding: '16px 20px', borderRadius: 8, border: '1px solid var(--border-subtle)', background: 'var(--bg-card)' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>Avg Approval Rate</div>
          <div className="mono" style={{ fontSize: 24, fontWeight: 700, color: avgApproval >= 0.8 ? 'var(--green)' : 'var(--amber)' }}>
            {totalDecisions > 0 ? `${(avgApproval * 100).toFixed(0)}%` : '0%'}
          </div>
        </div>
        <div style={{ padding: '16px 20px', borderRadius: 8, border: '1px solid var(--border-subtle)', background: 'var(--bg-card)' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>Exception Types</div>
          <div style={{ fontSize: 22, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{stats.length}</div>
        </div>
      </div>

      {stats.length === 0 ? (
        <div className="card" style={{ padding: 24, textAlign: 'center' }}>
          <p style={{ color: 'var(--text-tertiary)' }}>No decisions recorded yet.</p>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-default)' }}>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, color: 'var(--text-secondary)' }}>Exception Type</th>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, color: 'var(--text-secondary)' }}>Framework</th>
                <th style={{ padding: '10px 16px', textAlign: 'right', fontSize: 12, color: 'var(--text-secondary)' }}>Decisions</th>
                <th style={{ padding: '10px 16px', textAlign: 'right', fontSize: 12, color: 'var(--text-secondary)' }}>Approval</th>
                <th style={{ padding: '10px 16px', textAlign: 'right', fontSize: 12, color: 'var(--text-secondary)' }}>Confidence</th>
                <th style={{ padding: '10px 16px', textAlign: 'center', fontSize: 12, color: 'var(--text-secondary)' }}>Last Decision</th>
              </tr>
            </thead>
            <tbody>
              {stats.map((d, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                  <td style={{ padding: '8px 16px', fontSize: 13 }}>
                    <span style={{ color: iconColor(d.approval_rate), marginRight: 6 }}>{icon(d.approval_rate)}</span>
                    {titleCase(d.exception_type)}
                  </td>
                  <td style={{ padding: '8px 16px', fontSize: 13, color: 'var(--text-secondary)' }}>{d.framework}</td>
                  <td className="mono" style={{ padding: '8px 16px', fontSize: 13, textAlign: 'right' }}>{d.total_decisions}</td>
                  <td className="mono" style={{ padding: '8px 16px', fontSize: 13, textAlign: 'right', color: iconColor(d.approval_rate) }}>
                    {(d.approval_rate * 100).toFixed(0)}%
                  </td>
                  <td className="mono" style={{ padding: '8px 16px', fontSize: 13, textAlign: 'right' }}>{d.avg_ai_confidence != null ? d.avg_ai_confidence.toFixed(0) : '-'}</td>
                  <td style={{ padding: '8px 16px', fontSize: 12, textAlign: 'center' }}>
                    {d.last_decision_at ? (
                      <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                        {new Date(d.last_decision_at).toLocaleDateString()}
                      </span>
                    ) : (
                      <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
